import { Button, Col, DatePicker, DatePickerProps, Form, InputNumber, Row, Select, Table, Tag } from "antd";
import Column from "antd/es/table/Column";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { PlusOutlined } from '@ant-design/icons';
import { ContentHeader } from "../../components";
import ModalTextForm from "../../components/modal/ModalTextForm";
import { Links } from "../../helpers/Links";
import { dateCommon, validateMessages } from '../../helpers/common';
import { khoiTaoBangLuongPhuCap, layDsBangLuongPhuCapTamUngTheoNam, timKiemDsBangLuongPhuCap } from "../../services/luong";
import { hideLoadingScreen, showLoadingScreen } from "../../store/reducers/loading";
import { LuongPhuCapInfo } from "../../models/luong/LuongPhuCapModel";

const optionsLoai = [
    { value: 0, label: "Điều chỉnh" },
    { value: 1, label: "Tạm ứng" },
];

const DanhSachLuongPhuCap = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const ngayHienTai = (dayjs()).date() <= 10 ? (dayjs()).subtract(1, 'month') : dayjs();

    const [loading, setLoading] = useState<boolean>(false);
    const [dataDs, setDataDs] = useState<Array<LuongPhuCapInfo>>([]);
    const [ngayThang, setNgayThang] = useState<Dayjs | null>(null);
    const [loaiFilter, setLoaiFilter] = useState<number | undefined>(undefined);

    const [openModalKhoiTao, setOpenModalKhoiTao] = useState<boolean>(false);
    const [kyLuongKhoiTao, setKyLuongKhoiTao] = useState<Dayjs | null>(ngayHienTai);
    const [namLocTamUng, setNamLocTamUng] = useState<Dayjs | null>(null);

    const loaiWatch = Form.useWatch('loai', form);
    const [dsTamUng, setDsTamUng] = useState<Array<any>>([]);

    const layDsTamUngModal = async (nam?: number) => {
        try {
            const tamung = await layDsBangLuongPhuCapTamUngTheoNam(nam ?? null);
            setDsTamUng(tamung ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
    };

    const layDuLieu = async (thangNam?: string, loai?: number) => {
        setLoading(true);
        try {
            const rs = await timKiemDsBangLuongPhuCap({ start: 0, length: 1000, thangNam, loai });
            setDataDs(rs?.data ?? []);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
        setLoading(false);
    };

    useEffect(() => {
        const thangNam = ngayThang ? ngayThang.format('MM/YYYY') : undefined;
        layDuLieu(thangNam, loaiFilter);
    }, []);

    const monthFormat = 'MM/YYYY';
    const thangNamFormat: DatePickerProps['format'] = (value) => `Tháng ${value.format(monthFormat)}`;

    const onChangeThangNam: DatePickerProps['onChange'] = (date) => {
        setNgayThang(date);
        layDuLieu(date ? date.format('MM/YYYY') : undefined, loaiFilter);
    };

    const onChangeLoaiFilter = (val: number | undefined) => {
        setLoaiFilter(val);
        layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined, val);
    };

    const onMoModalKhoiTao = async () => {
        const ky = ngayThang ?? ngayHienTai;
        setKyLuongKhoiTao(ky);
        const namKy = dayjs().year(ky.year());
        setNamLocTamUng(namKy);
        form.setFieldsValue({
            kyLuong: ky,
            loai: 1,
            idLuongTamUng: undefined,
            soCongAnCa: undefined,
            soCongCTP: undefined,
        });
        await layDsTamUngModal(ky.year());
        setOpenModalKhoiTao(true);
    };

    const onChangeKyLuongKhoiTao = async (date: Dayjs | null) => {
        setKyLuongKhoiTao(date);
    };

    const onChangeNamLocTamUng = async (date: Dayjs | null) => {
        setNamLocTamUng(date);
        form.setFieldsValue({ idLuongTamUng: undefined });
        await layDsTamUngModal(date ? date.year() : undefined);
    };

    const onFinishKhoiTao = async (values: any) => {
        dispatch(showLoadingScreen());
        try {
            const ky = values?.kyLuong as Dayjs;
            const request = {
                thangNam: ky.format('MM/YYYY'),
                loai: parseInt(values?.loai),
                idLuongTamUng: values?.idLuongTamUng ? parseInt(values.idLuongTamUng) : null,
                soCongAnCa: values?.soCongAnCa
                    ? parseInt(values.soCongAnCa)
                    : null,
                soCongCTP: values?.soCongCTP
                    ? parseInt(values.soCongCTP)
                    : null,
            };
            const rs = await khoiTaoBangLuongPhuCap(request);
            if (rs > 0) {
                toast.success('Khởi tạo phụ cấp thành công!');
                setOpenModalKhoiTao(false);
                layDuLieu(ngayThang ? ngayThang.format('MM/YYYY') : undefined, loaiFilter);
            } else {
                toast.error('Khởi tạo phụ cấp thất bại!');
            }
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
        dispatch(hideLoadingScreen());
    };

    return (
        <section>
            {openModalKhoiTao && (
                <ModalTextForm
                    title="Khởi tạo phụ cấp"
                    hideFooter
                    show={openModalKhoiTao}
                    handleClose={() => setOpenModalKhoiTao(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinishKhoiTao}
                        form={form}
                    >
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    rules={[{ required: true }]}
                                    label="Kỳ phụ cấp"
                                    name="kyLuong"
                                >
                                    <DatePicker
                                        changeOnBlur={true}
                                        picker="month"
                                        format={["MM/YYYY", "MMYYYY"]}
                                        placeholder="Chọn kỳ phụ cấp"
                                        style={{ width: "100%" }}
                                        onChange={onChangeKyLuongKhoiTao}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    rules={[{ required: true }]}
                                    label="Loại"
                                    name="loai"
                                >
                                    <Select
                                        notFoundContent="Không có dữ liệu"
                                        options={optionsLoai}
                                        placeholder="Loại"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        {loaiWatch === 1 && (
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    label="Số công ăn ca"
                                    name="soCongAnCa"
                                    rules={[
                                        {
                                            required: loaiWatch === 1,
                                            message: 'Vui lòng nhập số công ăn ca'
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        min={0}
                                        precision={0}
                                        style={{ width: '100%' }}
                                        placeholder="Nhập số công ăn ca"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    label="Số công công tác phí"
                                    name="soCongCTP"
                                    rules={[
                                        {
                                            required: loaiWatch === 1,
                                            message: 'Vui lòng nhập số công công tác phí'
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        min={0}
                                        precision={0}
                                        style={{ width: '100%' }}
                                        placeholder="Nhập số công công tác phí"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>)}
                        {loaiWatch === 0 && (
                            <Row gutter={8}>
                                <Col span={8}>
                                    <Form.Item label="Năm tạm ứng">
                                        <DatePicker
                                            changeOnBlur={true}
                                            picker="year"
                                            format="YYYY"
                                            value={namLocTamUng}
                                            style={{ width: "100%" }}
                                            onChange={onChangeNamLocTamUng}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item
                                        rules={[]}
                                        label="Kỳ phụ cấp tạm ứng"
                                        name="idLuongTamUng"
                                    >
                                        <Select
                                            notFoundContent="Không có dữ liệu"
                                            options={(dsTamUng || []).map((x: any) => ({
                                                value: x.id,
                                                label: x.tenKyLuong,
                                            }))}
                                            placeholder="Chọn kỳ tạm ứng"
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col span={24} style={{ textAlign: "right", marginTop: 10 }}>
                                <Button htmlType="button" onClick={() => setOpenModalKhoiTao(false)}>Đóng</Button>
                                <Button type="primary" htmlType="submit" style={{ marginLeft: 8 }} icon={<PlusOutlined />}>Khởi tạo</Button>
                            </Col>
                        </Row>
                    </Form>
                </ModalTextForm>
            )}

            <ContentHeader title="Danh sách phụ cấp" />

            <Row className="form-group" gutter={8}>
                <Col span={6}>
                    <DatePicker
                        changeOnBlur={true}
                        value={ngayThang}
                        style={{ width: "100%" }}
                        placeholder="Tháng"
                        picker="month"
                        format={thangNamFormat}
                        onChange={onChangeThangNam}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        allowClear
                        style={{ width: "100%" }}
                        placeholder="Loại phụ cấp"
                        options={optionsLoai}
                        value={loaiFilter}
                        onChange={onChangeLoaiFilter}
                    />
                </Col>
                <Col span={12}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        onClick={onMoModalKhoiTao}
                        icon={<PlusOutlined />}
                    >
                        Khởi tạo phụ cấp
                    </Button>
                </Col>
            </Row>

            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Row>
                            <Col span={24}>
                                <Table
                                    locale={{ emptyText: "Không có dữ liệu" }}
                                    dataSource={(dataDs || []).map((value, index) => ({ key: index, ...value }))}
                                    loading={loading}
                                >
                                    <Column
                                        title="STT"
                                        dataIndex="stt"
                                        width={50}
                                        render={(value, item, index) => <>{index + 1}</>}
                                    />
                                    <Column
                                        title="Kỳ phụ cấp"
                                        key="tenKyLuong"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.tenKyLuong}</div>
                                        )}
                                    />
                                    <Column
                                        title="Loại"
                                        key="loai"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">
                                                {value?.loai === 0 ? "Điều chỉnh" : "Tạm ứng"}
                                            </div>
                                        )}
                                    />
                                    <Column
                                        title="Trạng thái"
                                        key="trangThai"
                                        align="left"
                                        render={(value) => (
                                            value?.trangThai === 0 ? (<Tag color="blue">Khởi tạo</Tag>) : (<Tag color="success">Đã xác nhận</Tag>)
                                        )}
                                    />
                                    <Column
                                        title="Người khởi tạo"
                                        key="tenNVKhoiTao"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{value?.tenNVKhoiTao}</div>
                                        )}
                                    />
                                    <Column
                                        title="Ngày khởi tạo"
                                        key="ngayKhoiTao"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">{dateCommon.formatDate(value?.ngayKhoiTao)}</div>
                                        )}
                                    />
                                    <Column
                                        title="Cập nhật gần nhất"
                                        key="ngayCapNhat"
                                        align="left"
                                        render={(value) => (
                                            <div className="text-align-middle">
                                                {dateCommon.formatDate(value?.ngayCapNhat ?? value?.ngayKhoiTao)}
                                            </div>
                                        )}
                                    />
                                    <Column
                                        title=""
                                        key="action"
                                        render={(value, _record, index) => (
                                            <Link
                                                key={"link" + index}
                                                className="hrm-btn hrm-btn-sm btn-primary"
                                                to={`${Links.Luong.DanhSachLuongPhuCap}/chitiet?id=${value?.id}`}
                                            >
                                                Xem
                                            </Link>
                                        )}
                                    />
                                </Table>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default DanhSachLuongPhuCap;
